import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { notification } from '../../../../utils/messaging';
import SelectTimeControl from '../../SelectTimeControl';

/**
 * A SelectTimeControl component which automatically fetches the available-to-select
 * times for a given lake, with specified arrival and departure dates
 */
export function LiveAvailabilityTimes({
  fisheryPublicId,
  selectedLakeId,
  arrivalTime,
  onChangeArrival,
  departureTime,
  onChangeDeparture,
  arrivalDate,
  departureDate,
  lakeExclusive,
  bookingType,
}) {
  const [inFlightTimeRequests, setInFlightTimeRequests] = useState(0);
  const registerTimeRequest = () => setInFlightTimeRequests((prev) => prev + 1);
  const unregisterTimeRequest = () =>
    setInFlightTimeRequests((prev) => Math.max(prev - 1, 0));
  const loadingTime = Boolean(inFlightTimeRequests);

  const [availableTime, setAvailableTime] = useState(null);

  const getAvailability = useCallback((payload_data) => {
    registerTimeRequest();
    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };
    axios
      .post('booking/lake/availability', payload_data, axios_config)
      .then((res) => {
        if (res.status === 200) {
          let res_data = res.data.result;

          if (res_data.is_available) {
            setAvailableTime(res_data);
          } else {
            notification.warning({
              description: res_data.message,
            });
          }
        }
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 500) {
            notification.error({
              description: 'Server Response failed. Please try again later',
            });
          } else if (err.response.status === 401) {
            notification.error({
              description:
                'Your action will not be saved. Please Login and try again!',
            });
          } else if (err.response.status === 400) {
            notification.warning({
              description: err.response.data['detail'],
            });
          } else if (err.response.status === 422) {
            if (err.response.data['detail'] === 'Signature has expired') {
              notification.error({
                description:
                  'Login session has been expired. Please Login to continue!',
              });
            } else {
              notification.error({
                description:
                  "OOPS! Server didn't like the request. If this persists please contact support team",
              });
            }
          } else if (err.response.status === 423) {
            notification.error({
              description:
                'Sorry ! this booking slot has been booked by another user, Try different booking!',
            });
          }
        } else {
          notification.error({
            description: 'Error processing result',
          });
        }
      })
      .finally(() => {
        unregisterTimeRequest();
      });
  }, []);

  useEffect(() => {
    setAvailableTime(null);
    if (!arrivalDate || !departureDate || !selectedLakeId) {
      return;
    }
    getAvailability({
      start_date: arrivalDate,
      end_date: departureDate,
      lake_id: selectedLakeId,
      book_lake_exclusive: lakeExclusive,
      booking_type: bookingType ?? '',
    });
  }, [
    arrivalDate,
    bookingType,
    departureDate,
    getAvailability,
    lakeExclusive,
    selectedLakeId,
  ]);

  if (!loadingTime && (!availableTime || !arrivalDate || !departureDate)) {
    return null;
  }

  return (
    <SelectTimeControl
      loading={loadingTime}
      fisheryPublicId={fisheryPublicId}
      availability={availableTime}
      selectedLakeId={selectedLakeId}
      arrivalTime={arrivalTime}
      onChangeArrival={onChangeArrival}
      departureTime={departureTime}
      onChangeDeparture={onChangeDeparture}
      arrivalDate={arrivalDate}
      departureDate={departureDate}
    />
  );
}
